import React, { useState, useRef } from 'react';
import { Wallet, Search, Trash2, Pencil, Eye } from 'lucide-react';
import { useData } from '../context/DataContext';
import { fmtRM2, fmtDate } from '../lib/store';
import { PageHeader, StatusBadge, EmptyState, Card } from '../components/Shared';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SignatureBlock, DocLogo } from '../components/DocumentDialog';
import { BrandingOverrideSection, ReferenceNumberField } from '../components/BrandingFields';
import ShareDocActions from '../components/ShareDocActions';
import { VoucherCategoryManager, DEFAULT_VOUCHER_CATEGORIES, DEFAULT_VOUCHER_METHODS } from '../components/VoucherCategoryManager';
import { toast } from 'sonner';

export default function PaymentVouchers() {
  const { vouchers, company, addItem, updateItem, removeItem, generateNumber, consumeNumber, updateCompany, renameVoucherField } = useData();
  const categories = company.voucherCategories || DEFAULT_VOUCHER_CATEGORIES;
  const methods = company.voucherMethods || DEFAULT_VOUCHER_METHODS;
  const setCategories = (list) => updateCompany({ voucherCategories: list });
  const setMethods = (list) => updateCompany({ voucherMethods: list });
  const onRenameField = (field) => (from, to) => {
    renameVoucherField(field, from, to);
    setForm((f) => (f && f[field] === from ? { ...f, [field]: to } : f));
  };
  const withCurrent = (list, current) => (current && !list.includes(current) ? [current, ...list] : list);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [preview, setPreview] = useState(null);
  const printRef = useRef(null);

  const openNew = () => {
    const today = new Date().toISOString().slice(0, 10);
    setForm({
      number: generateNumber('vouchers', today), payee: '', date: today, amount: 0,
      method: methods[0] || '', category: categories[0] || '', status: 'draft', notes: '',
    });
    setEditId(null); setOpen(true);
  };
  const openEdit = (v) => { setForm(v); setEditId(v.id); setOpen(true); };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = () => {
    if (!form.payee.trim() || !form.amount) { toast.error('Payee and amount are required'); return; }
    const payload = { ...form, amount: Number(form.amount) };
    if (editId) { updateItem('vouchers', editId, payload); toast.success('Voucher updated'); }
    else { addItem('vouchers', payload); consumeNumber('vouchers', payload.number); toast.success('Voucher created'); }
    setOpen(false);
  };

  const filtered = vouchers.filter((v) => v.payee.toLowerCase().includes(q.toLowerCase()) || (v.number || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="animate-fade-in-up">
      <PageHeader title="Payment Vouchers" subtitle="Record supplier or training payments — preview, PDF and share." actionLabel="New Voucher" onAction={openNew} />

      <Card className="mb-4 p-3">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vouchers..." className="pl-9" />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <EmptyState icon={Wallet} title="No vouchers" subtitle="Record a supplier payment." />
      ) : (
        <Card className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Number</TableHead>
                <TableHead className="font-semibold text-slate-600">Payee</TableHead>
                <TableHead className="font-semibold text-slate-600">Category</TableHead>
                <TableHead className="font-semibold text-slate-600">Date</TableHead>
                <TableHead className="font-semibold text-slate-600">Status</TableHead>
                <TableHead className="text-right font-semibold text-slate-600">Amount</TableHead>
                <TableHead className="text-right font-semibold text-slate-600">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((v) => (
                <TableRow key={v.id} className="hover:bg-slate-50">
                  <TableCell className="font-semibold text-orange-600">{v.number || '—'}</TableCell>
                  <TableCell className="text-slate-700">{v.payee}</TableCell>
                  <TableCell className="text-slate-500">{v.category}</TableCell>
                  <TableCell className="text-slate-500">{fmtDate(v.date)}</TableCell>
                  <TableCell><StatusBadge status={v.status} /></TableCell>
                  <TableCell className="text-right font-semibold text-slate-800">{fmtRM2(v.amount)}</TableCell>
                  <TableCell className="text-right">
                    <button title="Preview" onClick={() => setPreview(v)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid={`voucher-preview-${v.id}`}><Eye className="h-4 w-4" /></button>
                    <button title="Edit" onClick={() => openEdit(v)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>
                    <button title="Delete" onClick={() => { removeItem('vouchers', v.id); toast.success('Deleted'); }} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? 'Edit Voucher' : 'New Payment Voucher'}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <ReferenceNumberField value={form.number} onChange={(v) => setField('number', v)} testid="voucher-number-input" />
              </div>
              <div className="sm:col-span-2"><Label>Payee</Label><Input className="mt-1.5" value={form.payee} onChange={(e) => setField('payee', e.target.value)} data-testid="voucher-payee" /></div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Category</Label>
                  <VoucherCategoryManager categories={categories} onChange={setCategories} onRename={onRenameField('category')} />
                </div>
                <Select value={form.category} onValueChange={(v) => setField('category', v)}>
                  <SelectTrigger className="mt-1.5" data-testid="voucher-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {withCurrent(categories, form.category).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Method</Label>
                  <VoucherCategoryManager categories={methods} onChange={setMethods} onRename={onRenameField('method')} label="Payment method" title="Payment Methods" testid="method" />
                </div>
                <Select value={form.method} onValueChange={(v) => setField('method', v)}>
                  <SelectTrigger className="mt-1.5" data-testid="voucher-method-select"><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    {withCurrent(methods, form.method).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" className="mt-1.5" value={form.date} onChange={(e) => setField('date', e.target.value)} /></div>
              <div><Label>Amount (RM)</Label><Input type="number" className="mt-1.5" value={form.amount} onChange={(e) => setField('amount', e.target.value)} data-testid="voucher-amount" /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setField('status', v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{['draft', 'paid'].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2"><Label>Notes</Label><Input className="mt-1.5" value={form.notes} onChange={(e) => setField('notes', e.target.value)} /></div>
              <div className="sm:col-span-2">
                <BrandingOverrideSection form={form} setField={setField} company={company} testid="voucher-branding-section" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="voucher-save-btn">{editId ? 'Save Changes' : 'Create Voucher'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent aria-describedby={undefined} className="max-h-[92vh] max-w-2xl overflow-y-auto p-0">
          <DialogHeader className="sr-only"><DialogTitle>Payment Voucher {preview?.number || ''}</DialogTitle></DialogHeader>
          {preview && (
            <div>
              <div className="bg-white p-8" ref={printRef} data-testid="voucher-print-area">
                <div className="mb-6 flex items-start justify-between border-b-2 pb-6" style={{ borderColor: '#e2e8f0' }}>
                  <div>
                    <DocLogo doc={preview} company={company} accentBg="bg-orange-600" />
                    <p className="mt-3 text-sm font-semibold text-slate-800">{company.name}</p>
                    <p className="max-w-xs text-xs text-slate-500">{company.address}</p>
                    <p className="text-xs text-slate-500">{company.phone} · {company.email}</p>
                    <p className="text-xs text-slate-500">Reg No: {company.regNo}</p>
                  </div>
                  <div className="text-right">
                    <h2 className="text-2xl font-extrabold text-orange-600">PAYMENT VOUCHER</h2>
                    <p className="mt-2 text-sm font-semibold text-slate-800">{preview.number || '—'}</p>
                    <p className="text-xs text-slate-500">Date: {fmtDate(preview.date)}</p>
                    <p className="text-xs uppercase tracking-wide text-slate-400">{preview.status}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Paid To</p>
                  <p className="mt-1 text-base font-semibold text-slate-800">{preview.payee}</p>
                </div>

                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
                      <th className="py-2">Description</th>
                      <th className="py-2">Category</th>
                      <th className="py-2">Method</th>
                      <th className="py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-100">
                      <td className="py-2.5 text-slate-700">{preview.notes || 'Payment'}</td>
                      <td className="py-2.5 text-slate-600">{preview.category}</td>
                      <td className="py-2.5 text-slate-600">{preview.method}</td>
                      <td className="py-2.5 text-right font-medium text-slate-800">{fmtRM2(preview.amount)}</td>
                    </tr>
                  </tbody>
                </table>

                <div className="mt-4 flex justify-end">
                  <div className="w-full max-w-xs">
                    <div className="flex justify-between rounded-lg bg-orange-600 px-3 py-2 text-base font-bold text-white"><span>Total Paid</span><span>{fmtRM2(preview.amount)}</span></div>
                  </div>
                </div>

                <div className="mt-10 flex items-end justify-between gap-8">
                  <SignatureBlock doc={preview} company={company} />
                  <div className="w-48">
                    <div className="h-16" />
                    <div className="border-t border-slate-400 pt-1">
                      <p className="text-xs font-semibold text-slate-800">Received By</p>
                      <p className="text-[11px] text-slate-400">Name, signature &amp; date</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 border-t border-slate-200 pt-4">
                  <p className="text-xs text-slate-400">Bank: {company.bank} · A/C: {company.bankAccount}</p>
                </div>
              </div>
              <DialogFooter className="border-t border-slate-100 bg-slate-50 px-6 py-3">
                <Button variant="outline" onClick={() => setPreview(null)}>Close</Button>
                <ShareDocActions
                  targetRef={printRef}
                  docLabel="Payment Voucher"
                  number={preview.number}
                  recipientName={preview.payee}
                  amountText={fmtRM2(preview.amount)}
                  companyName={company.name}
                />
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
